import React, {useEffect, useMemo, useState} from 'react';
import {
    Box,
    Stack,
    TextField,
    Typography,
    MenuItem,
    Checkbox
} from '@mui/material';
import CircleIcon from '@mui/icons-material/Circle';
import {generateUUID} from '../../../utility/uuidUtil';
import IconButton from '@mui/material/IconButton';
import ContentCopyRoundedIcon from '@mui/icons-material/ContentCopyRounded';
import Tooltip from '@mui/material/Tooltip';
import PropTypes from 'prop-types';
import EmailsInput from '../../../shared-components/EmailsInput';
import FormExpirationDatePicker from './FormExpirationDatePicker';
import useNotification from '../../../utility/hooks/useNotification';
import useTextField from '../../../utility/hooks/useTextField';
import {validateFieldLength} from '../../../utility/validationUtil';
import FormAccessSettings from './FormAccessSettings';
import AutoresponderSettings from './PrivateFormSettings/AutoresponderSettings';
import SettingsSectionBase from './PrivateFormSettings/SettingsSection';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import LinkOutlinedIcon from '@mui/icons-material/LinkOutlined';
import CodeOutlinedIcon from '@mui/icons-material/CodeOutlined';
import FormTemplateType from '../../../constants/FormTemplateType';
import FORM_TYPE from '../../../constants/FormType';

export const getFormUrl = (formId) => `${window.origin}/forms-request/${formId}`;

export const getIFrameHtml = (formId, frameId) => `
<iframe src="${getFormUrl(formId)}/?embedded=true"
        id='${frameId}'
        frameborder="0"
        marginheight="0"
        marginwidth="0"
        width="100%">
</iframe>
<script>
    function iframeResizeListener(event) {
        if (event.data && event.data.type === 'iframeResizer' && event.data.iframeHeight) {
            let iframe = document.getElementById('${frameId}');
            iframe.style.height = event.data.iframeHeight + 'px';
        }
    }
    window.addEventListener('message', iframeResizeListener, false);
</script>`;

export const getIFrame = (formId) =>
    getIFrameHtml(formId, generateUUID())
        .replace(/[\n\r\t]+|\s\s+/g, ' ')
        .trim();

export const shouldShowPublicSharingSettings = ({formType, templateType}) => {
    if (formType) {
        return [
            FORM_TYPE.application,
            FORM_TYPE.shareable,
        ].includes(formType);
    }

    return [
        FormTemplateType.acceptance,
        FormTemplateType.application,
        FormTemplateType.shareable,
    ].includes(templateType);
};

const PublicFormSettings = ({
                                formId,
                                notificationEmails,
                                onNotificationEmailsChange,
                                expirationDate,
                                onExpirationDateChange,
                                isFormArchived,
                                name,
                                onNameChange,
                                categories,
                                onCategoriesChange,
                                formCategories,
                                formAccessRef,
                                schema,
                                enableAutoresponder,
                                onAutoresponderToggle,
                                accessLevel,
                                audience,
                                recipientType,
                                linkedToStudents,
                                linkedToEmployees,
                                isCommunicationForm,
                                templateType,
                                formType,
                                templateName,
                                requestedSection
                            }) => {
    // AD-2689 (Maria, 14 October 2026): application, acceptance and public forms have the same Form Settings sidebar as
    // every other form, with the same names as the Create Form box, and open on Public Link.
    const [activeSettingsSection, setActiveSettingsSection] = useState('link');
    // AD-2693: the save pop-up opens the section where something is missing.
    useEffect(() => {
        if (requestedSection?.section) setActiveSettingsSection(requestedSection.section);
    }, [requestedSection]);

    const SETTINGS_SIDE_CLEARANCE = 'clamp(160px, 16vw, 240px)';
    const iframe = useMemo(() => getIFrame(formId), [formId]);
    const formUrl = getFormUrl(formId);

    const {
        showErrorNotification,
        showSuccessNotification,
        showDetailedErrorNotification
    } = useNotification();

    const nameField = useTextField({
        initialValue: name ?? '',
        validate: (value) => validateFieldLength(value, 128),
        onChange: (event) => onNameChange(event.target.value)
    });

    const copyToClipBoard = async (src, label) => {
        try {
            await navigator.clipboard.writeText(src);
            showSuccessNotification(`${label} copied to clipboard`);
        } catch (e) {
            if (showDetailedErrorNotification) {
                showDetailedErrorNotification(
                    'Unable to copy to clipboard',
                    e?.message || 'Please copy the value manually.'
                );
            } else if (showErrorNotification) {
                showErrorNotification('Unable to copy to clipboard');
            }
        }
    };

    const selectedCategories = categories || [];
    const showPublicSharingSettings = shouldShowPublicSharingSettings({
        formType,
        templateType
    });

    const handleCategoryChange = (e) => {
        onCategoriesChange(e.target.value);
    };

    const formTypeName = formType === FORM_TYPE.shareable
        ? 'Public form for anyone on the web (not associated with students or employees)'
        : 'Application or Acceptance form';
    const settingsPanes = [
        {
            key: 'formType',
            label: 'Form Type',
            readOnly: true,
            show: true,
            node: (
                <SettingsSectionBase header="Form Type" headerIcon={<SettingsOutlinedIcon fontSize="small"/>}>
                    <TextField fullWidth label="Form Type" value={formTypeName} disabled/>
                </SettingsSectionBase>
            ),
        },
        {
            key: 'template',
            label: 'Template',
            readOnly: true,
            show: true,
            node: (
                <SettingsSectionBase header="Template" headerIcon={<DescriptionOutlinedIcon fontSize="small"/>}>
                    <TextField
                        fullWidth
                        label="Template"
                        value={templateName || 'Blank form'}
                        disabled
                        data-testid="form-settings-template"
                    />
                </SettingsSectionBase>
            ),
        },
        {
            key: 'link',
            label: 'Public Link',
            readOnly: false,
            show: showPublicSharingSettings,
            node: (
                <>
                    <PublicLinkSettingsSection formUrl={formUrl} onCopy={copyToClipBoard}/>
                    <PublicEmbedSettingsSection iframe={iframe} onCopy={copyToClipBoard}/>
                </>
            ),
        },
        {
            key: 'name',
            label: 'Form Name',
            readOnly: isFormArchived,
            show: true,
            node: (
                <SettingsSectionBase header="Form Name" headerIcon={<DescriptionOutlinedIcon fontSize="small"/>}>
                        <TextField
                            fullWidth
                            label="Form Name"
                            required={nameField.required}
                            disabled={isFormArchived}
                            value={nameField.value}
                            onChange={nameField.onChange}
                            error={nameField.error}
                            helperText={nameField.helperText || ' '}
                            autoComplete="off"
                        />

                </SettingsSectionBase>
            ),
        },
        {
            key: 'category',
            label: 'Category',
            readOnly: isFormArchived,
            show: true,
            node: (
                <SettingsSectionBase header="Category" headerIcon={<DescriptionOutlinedIcon fontSize="small"/>}>
                        <TextField
                            select
                            fullWidth
                            disabled={isFormArchived}
                            value={selectedCategories}
                            label="Categories"
                            helperText=" "
                            SelectProps={{
                                multiple: true,
                                renderValue: (selected) => {
                                    if (!formCategories?.length) {
                                        return <em>No categories</em>;
                                    }

                                    if (!Array.isArray(selected) || selected.length === 0) {
                                        return <em>No categories</em>;
                                    }

                                    const selectedCats =
                                        formCategories?.filter(c =>
                                            selected.includes(c.id)
                                        ) || [];

                                    return (
                                        <Stack
                                            flexDirection="row"
                                            gap={1}
                                            flexWrap="wrap"
                                        >
                                            {selectedCats.map(cat => (
                                                <Box
                                                    key={cat.id}
                                                    sx={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: 0.8,
                                                        padding: 1,
                                                        borderRadius: '18px',
                                                        border: '1px solid #adadad'
                                                    }}
                                                >
                                                    <CircleIcon
                                                        fontSize="small"
                                                        sx={{color: cat.color}}
                                                    />
                                                    <Typography variant="body2">
                                                        {cat.name}
                                                    </Typography>
                                                </Box>
                                            ))}
                                        </Stack>
                                    );
                                },
                                MenuProps: {
                                    sx: {
                                        '& .MuiMenuItem-root': {
                                            whiteSpace: 'unset',
                                            wordBreak: 'break-all'
                                        }
                                    }
                                }
                            }}
                            onChange={handleCategoryChange}
                        >
                            {!formCategories?.length && (
                                <MenuItem disabled>
                                    <em>No categories</em>
                                </MenuItem>
                            )}
                            {formCategories?.map(c => (
                                <MenuItem key={c.id} value={c.id}>
                                    <Checkbox checked={selectedCategories.includes(c.id)}/>
                                    <CircleIcon
                                        fontSize="small"
                                        sx={{
                                            color: c.color,
                                            mr: 1
                                        }}
                                    />
                                    {c.name}
                                </MenuItem>
                            ))}
                        </TextField>
                </SettingsSectionBase>
            ),
        },
        {
            key: 'expiry',
            label: 'Expiry',
            readOnly: isFormArchived,
            show: true,
            node: (
                <SettingsSectionBase header="Expiry" headerIcon={<SettingsOutlinedIcon fontSize="small"/>}>
                        <FormExpirationDatePicker
                            label="Expiry date (optional)"
                            expirationDate={expirationDate}
                            onExpirationDateChange={onExpirationDateChange}
                            disabled={isFormArchived}
                            sx={{maxWidth: 360}}
                        />
                </SettingsSectionBase>
            ),
        },
        {
            key: 'notifications',
            label: 'Notifications',
            readOnly: isFormArchived,
            show: true,
            node: (
                <SettingsSectionBase header="Notifications" headerIcon={<SettingsOutlinedIcon fontSize="small"/>}>
                        <EmailsInput
                            disabled={isFormArchived}
                            label="Notification email addresses"
                            values={notificationEmails}
                            onChange={emails =>
                                onNotificationEmailsChange(
                                    Array.from(
                                        new Map(emails.map(item => [item.email, item])).values()
                                    )
                                )
                            }
                            onBlur={email =>
                                onNotificationEmailsChange(prev =>
                                    Array.from(
                                        new Map([
                                            ...prev.map(item => [item.email, item]),
                                            [email, {email}]
                                        ]).values()
                                    )
                                )
                            }
                        />
                </SettingsSectionBase>
            ),
        },
        {
            key: 'access',
            label: 'Access',
            readOnly: isFormArchived,
            show: true,
            node: (
                <FormAccessSettings
                    embedded
                    ref={formAccessRef}
                    formId={formId}
                    formName={name}
                />
            ),
        },
        {
            key: 'autoresponses',
            label: 'Automatic Responses',
            readOnly: isFormArchived,
            show: !isCommunicationForm,
            node: (
                <AutoresponderSettings
                    embedded
                    formId={formId}
                    schema={schema}
                    enableAutoresponder={enableAutoresponder}
                    onAutoresponderToggle={onAutoresponderToggle}
                    accessLevel={accessLevel}
                    isFormArchived={isFormArchived}
                    audience={audience}
                    recipientType={recipientType}
                    linkedToStudents={linkedToStudents}
                    linkedToEmployees={linkedToEmployees}
                />
            ),
        },
    ].filter(pane => pane.show);
    const shownSettingsSection = settingsPanes.some(pane => pane.key === activeSettingsSection)
        ? activeSettingsSection
        : settingsPanes.find(pane => !pane.readOnly)?.key;

    return (
        <Box sx={{
            py: 2,
            px: {
                xs: 2,
                md: SETTINGS_SIDE_CLEARANCE,
            }
        }}>
            <Box sx={{display: 'flex', gap: 3, alignItems: 'flex-start', width: '100%', maxWidth: '1800px', mx: 'auto'}}>
                <Box
                    component="nav"
                    data-testid="form-settings-sidebar"
                    sx={{
                        width: 210,
                        flexShrink: 0,
                        position: 'sticky',
                        // AD-2705: below the logo bar and the editor's tab bar, which now stay in view too.
                        top: 'calc(var(--app-header-height, 0px) + var(--sticky-bar-height, 0px) + 16px)',
                        backgroundColor: '#fff',
                        border: '1px solid #e0e0e0',
                        borderRadius: 2,
                        py: 0.5,
                    }}
                >
                    {settingsPanes.map(pane => {
                        const selected = pane.key === shownSettingsSection;
                        return (
                            <Box
                                key={pane.key}
                                component="button"
                                data-testid={`form-settings-nav-${pane.key}`}
                                onClick={() => setActiveSettingsSection(pane.key)}
                                sx={{
                                    display: 'block',
                                    width: '100%',
                                    textAlign: 'left',
                                    px: 2,
                                    py: 0.75,
                                    border: 0,
                                    borderLeft: selected ? '3px solid #186BBE' : '3px solid transparent',
                                    backgroundColor: selected ? '#f2f7fd' : 'transparent',
                                    color: selected ? '#186BBE' : (pane.readOnly ? 'text.secondary' : 'text.primary'),
                                    fontWeight: selected ? 600 : 400,
                                    fontSize: 14,
                                    fontFamily: 'inherit',
                                    cursor: 'pointer',
                                    '&:hover': {backgroundColor: '#f5f7fa'},
                                }}
                            >
                                {pane.label}
                            </Box>
                        );
                    })}
                </Box>
                <Box sx={{flex: 1, minWidth: 0}}>
                    {settingsPanes.map(pane => (
                        <Box
                            key={pane.key}
                            data-settings-section={pane.key}
                            sx={{display: pane.key === shownSettingsSection ? 'flex' : 'none', flexDirection: 'column', gap: 3}}
                        >
                            {pane.node}
                        </Box>
                    ))}
                </Box>
            </Box>
        </Box>
    );
};

export const PublicLinkSettingsSection = ({formUrl, onCopy}) => (
    <SettingsSectionBase
        header="Public Link"
        headerIcon={<LinkOutlinedIcon fontSize="small"/>}
        subHeader="Share this URL directly with people who need to complete the form."
    >
        <Stack
            direction={{
                xs: 'column',
                sm: 'row'
            }}
            alignItems={{
                xs: 'stretch',
                sm: 'center'
            }}
            spacing={1.5}
        >
            <TextField
                fullWidth
                value={formUrl}
                label="Form URL"
                variant="outlined"
                InputProps={{readOnly: true}}
                size="small"
            />
            <Tooltip title="Copy to clipboard">
                <IconButton
                    onClick={() => onCopy(formUrl, 'URL')}
                    sx={{
                        alignSelf: {
                            xs: 'flex-end',
                            sm: 'center'
                        },
                        mt: {
                            xs: 0.5,
                            sm: 0
                        }
                    }}
                >
                    <ContentCopyRoundedIcon/>
                </IconButton>
            </Tooltip>
        </Stack>
    </SettingsSectionBase>
);

export const PublicEmbedSettingsSection = ({iframe, onCopy}) => (
    <SettingsSectionBase
        header="Embed"
        headerIcon={<CodeOutlinedIcon fontSize="small"/>}
        subHeader="Use this embed code to display the form inside another website."
    >
        <Stack
            direction={{
                xs: 'column',
                sm: 'row'
            }}
            alignItems={{
                xs: 'stretch',
                sm: 'flex-start'
            }}
            spacing={1.5}
        >
            <TextField
                fullWidth
                multiline
                minRows={3}
                maxRows={6}
                value={iframe}
                label="Embed code"
                variant="outlined"
                InputProps={{
                    readOnly: true,
                    sx: {
                        fontFamily:
                            'Monaco, Menlo, Consolas, "Courier New", monospace',
                        fontSize: 12,
                    },
                }}
                InputLabelProps={{
                    shrink: true,
                }}
                sx={{
                    '& .MuiInputLabel-outlined': {
                        backgroundColor: '#fff',
                        paddingRight: 1,
                    },
                }}
            />

            <Tooltip title="Copy to clipboard">
                <IconButton
                    onClick={() => onCopy(iframe, 'Embed code')}
                    sx={{
                        alignSelf: {
                            xs: 'flex-end',
                            sm: 'flex-start'
                        },
                        mt: {
                            xs: 0.5,
                            sm: 0
                        }
                    }}
                >
                    <ContentCopyRoundedIcon/>
                </IconButton>
            </Tooltip>
        </Stack>
    </SettingsSectionBase>
);

PublicFormSettings.propTypes = {
    requestedSection: PropTypes.shape({section: PropTypes.string, at: PropTypes.number}),
    formId: PropTypes.number,
    notificationEmails: PropTypes.array,
    onNotificationEmailsChange: PropTypes.func,
    expirationDate: PropTypes.string,
    onExpirationDateChange: PropTypes.func,
    isFormArchived: PropTypes.bool,
    name: PropTypes.string,
    onNameChange: PropTypes.func,
    categories: PropTypes.array,
    onCategoriesChange: PropTypes.func,
    formCategories: PropTypes.array,
    formAccessRef: PropTypes.oneOfType([
        PropTypes.func,
        PropTypes.shape({current: PropTypes.any})
    ]),
    schema: PropTypes.array,
    enableAutoresponder: PropTypes.bool,
    onAutoresponderToggle: PropTypes.func,
    accessLevel: PropTypes.number,
    audience: PropTypes.number,
    recipientType: PropTypes.string,
    linkedToStudents: PropTypes.bool,
    linkedToEmployees: PropTypes.bool,
    isCommunicationForm: PropTypes.bool,
    templateType: PropTypes.string,
    formType: PropTypes.string,
    templateName: PropTypes.string,
};

PublicLinkSettingsSection.propTypes = {
    formUrl: PropTypes.string.isRequired,
    onCopy: PropTypes.func.isRequired,
};

PublicEmbedSettingsSection.propTypes = {
    iframe: PropTypes.string.isRequired,
    onCopy: PropTypes.func.isRequired,
};

export default PublicFormSettings;
